import { GAMES_REQUIRED, sessionTotal } from '@league/shared/scoring';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { Button, ErrorNote } from '../../components/ui';
import type { ScoreCard as Card, ScoreCardResponse } from '@league/shared/contract';

import { ApiError, apiFetch } from '../../lib/api';
import { emptyGames, gamesFromReading, gamesFromSaved, gridProblems, gridTotals, hasAnyEntry, matchesSaved, readButEmpty as emptyButRead, gamesFromStorage, toEntries, type Game } from '../scores/grid';
import { ReadCard, type CardReading } from '../scores/ReadCard';
import { doubtfulCells, ReadingNotice } from '../scores/ReadingNotice';
import { ScoreGrid } from '../scores/ScoreGrid';

/**
 * HER TABLE'S SCORE CARD, on her own phone.
 *
 * THE CARD BELONGS TO THE TABLE, not to her. The paper version is one sheet that East fills in and
 * the four of them agree on before it is handed over, and this is the same: whoever picks up her
 * phone fills it in for everybody, and the four settle it between themselves the way they always
 * have. Four personal cards that must agree was the alternative, and it makes "three of us have
 * entered and the fourth has gone home" somebody's problem at eleven at session.
 *
 * It appears only when there is something to fill in — a published table, on a session that is not
 * closed. Everything else is the API's answer rendered, not a rule this component keeps.
 *
 * ⚠ THE DIRECTOR'S GRID, AND HER PHOTO READER — owner, 2026-10-04: players get "the same score card
 * functionality as the directors". This was a stack of per-game lists with a tap for who won; it is
 * now the same `ScoreGrid` the director's sheet uses, so who won is worked out from the numbers the
 * same way on both sides, and a card handed in reads back identically on her screen.
 */



/**
 * The card as it stands, as rows she can edit. An untouched card opens with the
 * {@link GAMES_REQUIRED} rows a table must finish, as the director's sheet does.
 */
function gamesFrom(card: Card): Game[] {
  const saved = gamesFromSaved(card.players);
  return saved.length > 0
    ? saved
    : Array.from({ length: GAMES_REQUIRED }, (_, index) => ({ gameNumber: index + 1, points: {} }));
}

/** Seat order, the same order the director's sheet goes across — owner, 2026-10-04. */
const SEAT_ORDER = ['east', 'south', 'west', 'north'];

/**
 * First names across the top — the WHOLE first name ("Mary Ann", not "Mary"), which is the name
 * less its surname — with the surname kept where two at the table share a first name.
 */
function columnsFor(card: Card) {
  const first = (name: string) => name.split(' ').slice(0, -1).join(' ') || name;
  const seat = (s: string) => (SEAT_ORDER.indexOf(s) + 5) % 5;
  return [...card.players].sort((a, b) => seat(a.seat) - seat(b.seat)).map((player) => ({
    playerId: player.playerId,
    label: card.players.some((other) => other.playerId !== player.playerId && first(other.name) === first(player.name))
      ? player.name
      : first(player.name),
    fullName: player.name,
    isYou: player.isYou,
  }));
}

/** Her own card, signed in. */
export function ScoreCard({ sessionId, playerId }: { sessionId: string; playerId: string }) {
  const queryClient = useQueryClient();
  return (
    <TableScoreCard
      endpoint={`/api/my/sessions/${sessionId}/score-card`}
      queryKey={['my-score-card', sessionId]}
      draftKey={playerId ? `table-score-draft:${playerId}:${sessionId}` : null}
      draftBelongs={(base) => base.sessionId === sessionId && base.players.some((p) => p.isYou && p.playerId === playerId)}
      onHandedIn={() => queryClient.invalidateQueries({ queryKey: ['my-today'] })}
    />
  );
}

/**
 * THE CARD ITSELF, wherever it is reached from: her phone signed in, or the QR on the table's
 * printed score sheet with nobody signed in (owner, 2026-10-06). The two differ only in the address
 * they read and hand in at, and in which saved draft is theirs.
 */
export function TableScoreCard({
  endpoint,
  queryKey,
  draftKey: storageKey,
  draftBelongs,
  onHandedIn,
}: {
  endpoint: string;
  queryKey: readonly unknown[];
  draftKey: string | null;
  /** A draft saved in this browser is only restored if it is for this same card. */
  draftBelongs: (base: Card) => boolean;
  onHandedIn?: () => Promise<unknown> | void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<{ base: Card; games: Game[] } | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [storageWarning, setStorageWarning] = useState(false);
  /** What the last photo of the card said, until it is handed in. */
  const [reading, setReading] = useState<CardReading | null>(null);
  const submitting = useRef(false);

  const card = useQuery({
    queryKey,
    queryFn: () => apiFetch<ScoreCardResponse>(endpoint),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchIntervalInBackground: false,
    refetchInterval: 30_000,
  });

  // Session-local recovery, scoped to the authenticated player's record. Never merge a saved
  // draft with a newer server version; the original version remains its submission precondition.
  useEffect(() => {
    if (!storageKey || loadedKey === storageKey) return;
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as { base: Card; games: unknown };
        const games = gamesFromStorage(parsed.games);
        if (parsed.base && draftBelongs(parsed.base)
          && typeof parsed.base.revision === 'string' && games) setDraft({ base: parsed.base, games });
      }
    } catch { setStorageWarning(true); }
    setLoadedKey(storageKey);
  }, [storageKey, loadedKey, draftBelongs]);

  useEffect(() => {
    if (!storageKey || loadedKey !== storageKey) return;
    try {
      if (draft) sessionStorage.setItem(storageKey, JSON.stringify(draft));
      else sessionStorage.removeItem(storageKey);
    } catch { setStorageWarning(true); }
  }, [draft, storageKey, loadedKey]);

  useEffect(() => {
    if (!draft) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [draft]);

  const submit = useMutation({
    mutationFn: async () => {
      const base = draft?.base ?? card.data!.card;
      /* Her own wording, naming who and which game (Courtney's, kept from the list this replaced). */
      const entries = toEntries(draft?.games ?? gamesFrom(base), base.players, (player, gameNumber) =>
        `Enter a whole-number score for ${player.name} in game ${gameNumber}, or clear all entries in this new game.`);
      if (!entries.length) throw new Error("Enter at least one completed game before handing in the card.");
      try {
        return await apiFetch<ScoreCardResponse>(endpoint, {
          method: 'PUT',
          body: JSON.stringify({ entries, revision: base.revision }),
        });
      } catch (err) {
        /* The hand-in may have arrived and only the answer been lost — see the director's sheet. */
        const fresh = await apiFetch<ScoreCardResponse>(endpoint).catch(() => null);
        if (fresh && matchesSaved(draft?.games ?? gamesFrom(base), fresh.card.players)) return fresh;
        throw err;
      }
    },
    onSuccess: async response => {
      setDraft(null);
      setReading(null);
      if (storageKey) {
        try { sessionStorage.removeItem(storageKey); } catch { setStorageWarning(true); }
      }
      queryClient.setQueryData(queryKey, response);
      await onHandedIn?.();
    },
    onError: () => { void card.refetch(); },
    onSettled: () => { submitting.current = false; },
  });

  const latest = card.data?.card;
  const it = draft?.base ?? latest;
  if (!it) {
    if (card.error && !(card.error instanceof ApiError && card.error.status === 404)) {
      return <ErrorNote>Could not load table scores. <Button variant="secondary" onClick={() => void card.refetch()}>Try again</Button></ErrorNote>;
    }
    return null;
  }
  const games = draft?.games ?? gamesFrom(it);
  const changed = Boolean(draft && (!latest || draft.base.revision !== latest.revision || card.error));
  const submitted = latest?.state === 'submitted' && !draft;
  const approved = latest?.state === 'approved';
  const canEdit = Boolean(!submitted && latest?.editable && !card.isError && !changed && !submit.isPending);
  const error = changed ? 'The table’s scores or seating changed. Your local entries are preserved and have not been submitted. Review the latest scores before entering changes again.' : card.isError ? 'Could not verify your current table scores. Any local entries are preserved.' : storageWarning ? 'This draft could not be saved in your browser. Keep this page open.' : submit.error instanceof TypeError || (submit.error instanceof ApiError && (submit.error.code === 'NO_BODY' || submit.error.code === 'NETWORK')) ? 'Could not submit your scores. Your entries are still here. Check your connection and try again.' : submit.error instanceof Error ? submit.error.message : null;
  const setGames = (update: (current: Game[]) => Game[]) => {
    submit.reset();
    setDraft(current => ({ base: current?.base ?? it, games: update(current?.games ?? gamesFrom(it)) }));
  };
  const mode = latest?.scoringMode ?? it.scoringMode;
  const totals = gridTotals(games, it.players, mode);
  /* Held, and named in the grid, exactly as the director's Save is. */
  const unsettled = gridProblems(games, it.players).length > 0 || emptyGames(games).length > 0 || emptyButRead(reading, games).length > 0;
  const nameOf = (playerId: string | null) => it.players.find((p) => p.playerId === playerId)?.name ?? null;

  return (
    /* Tighter on a phone, so four names fit across the grid without being cut short. */
    <section aria-busy={card.isFetching || submit.isPending} className="mt-6 rounded-md border border-light bg-paper p-3 sm:p-5">
      <h2 className="font-display text-[19px]">Table {it.tableNumber}&rsquo;s scores</h2>

      {/*
        WHOSE CARD THIS IS, said plainly. She is entering numbers for three other women, and a
        screen that did not say so would feel like a mistake the first time she saw it.
      */}
      <p className="mt-1 text-[13px] text-ink-2">
        One scorecard for your table.
      </p>

      {approved ? <p role="status" className="mt-3 text-[14px] font-semibold text-ink">Scores approved</p> : null}

      {latest?.lockedBecause ? (
        <p className="mt-3 rounded-md bg-sand px-3 py-2 text-[13px] text-ink-2">
          {latest.lockedBecause === 'Finalize seating before entering scores.'
            ? 'Scores open once tables are set.'
            : latest.lockedBecause === 'This session is closed. Reopen it before changing scores.'
              ? 'Scores are closed. Contact your Director if something needs correcting.'
              : latest.lockedBecause}
        </p>
      ) : latest?.state === 'submitted' ? (
        <p className="mt-3 text-[13px] text-sage">
          Handed in. You can still change it until your director approves.
        </p>
      ) : null}

      {(submitted || approved) && latest ? (
        <section aria-label="Table totals" className="mt-3 text-[13px]">
          <h3 className="font-semibold">Table totals</h3>
          <dl className="mt-1 space-y-1">
            {latest.players.map(player => (
              <div key={player.playerId} className="flex items-baseline justify-between gap-3">
                <dt>{approved && player.isYou ? <><span>{player.name}</span><span className="ml-2 text-[12px] text-ink-2"> you</span></> : player.name}</dt>
                <dd className="tabular">{totalPoints(player.games, latest.scoringMode)}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      {error ? (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
          {changed ? <Button variant="destructive" className="mt-2" onClick={() => {
            if (window.confirm('Discard your local entries and load the latest table scores?')) {
              setDraft(null); submit.reset(); void card.refetch();
            }
          }}>Discard local entries and load latest scores</Button> : card.isError ? <Button variant="secondary" onClick={() => void card.refetch()}>Try again</Button> : null}
        </div>
      ) : null}

      {card.data?.cardReading && canEdit ? (
        <div className="mt-4 flex flex-col gap-3">
          <ReadCard
            endpoint={`${endpoint}/read`}
            tableNumber={it.tableNumber}
            replacing={games.some(hasAnyEntry)}
            onRead={(read) => {
              /* A blank reading fills nothing, so it leaves what is typed alone — as on the director's sheet. */
              if (!read.blank && read.games.length > 0) setGames(() => gamesFromReading(read, GAMES_REQUIRED));
              setReading(read);
            }}
          />
          {reading ? <ReadingNotice reading={reading} totals={totals} nameOf={nameOf} then="hand it in" /> : null}
        </div>
      ) : null}

      {approved ? (
        <ol className="mt-5 flex flex-col gap-5">
          {games.map(game => (
            <li key={game.gameNumber}>
              <h3 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-ink-3">Game {game.gameNumber}</h3>
              <dl className="mt-2 space-y-2 text-[14px]">
                {it.players.map(player => (
                  <div key={player.playerId} className="flex items-baseline justify-between gap-3 rounded-md border border-light px-3 py-2">
                    <dt>{player.name}{game.saved?.won[player.playerId] ? <span className="sr-only">, recorded winner</span> : null}</dt>
                    <dd className="tabular">{game.points[player.playerId] ?? ''}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ol>
      ) : <div className="mt-4">
        <ScoreGrid
          columns={columnsFor(it)}
          games={games}
          onChange={setGames}
          mode={mode}
          disabled={!canEdit}
          doubtful={doubtfulCells(reading)}
          readButEmpty={emptyButRead(reading, games)}
        />
      </div>}

      {!approved && latest?.editable && !card.isError && !changed ? (
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Button
            className="min-h-[44px]"
            disabled={approved || submit.isPending || loadedKey !== storageKey || (canEdit && unsettled)}
            onClick={() => { if (submitted && latest) { setDraft({base: latest, games: gamesFrom(latest)}); return; } if (!submitting.current) { submitting.current = true; submit.mutate(); } }}
          >
            {submit.isPending ? 'Handing in…' : submitted ? 'Edit scores' : draft?.base.state === 'submitted' ? 'Hand in updated scores' : 'Hand in the card'}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Her session total, or a dash if it cannot be worked out. The total refuses games recorded under two
 * scoring modes; refusing while the card is drawn would blank the card (and the QR score sheet page),
 * so it says "—" instead (2026-10-09).
 */
function totalPoints(...args: Parameters<typeof sessionTotal>): number | string {
  try {
    return sessionTotal(...args).points;
  } catch {
    return '—';
  }
}
